import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Modal, Fab,
  FormGroup, FormControlLabel, Checkbox, Snackbar, Alert, Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

const API_BASE = 'https://feescrita-138417386089.us-central1.run.app';
const SUBSCRIBED_KEY = 'feescrita_subscribed';

const INTERESTS = [
  { key: 'familia', label: '👨‍👩‍👧‍👦 Família' },
  { key: 'fe_esperanca', label: '🙏 Fé e Esperança' },
  { key: 'trabalho', label: '💼 Trabalho' },
  { key: 'relacionamentos', label: '❤️ Relacionamentos' },
  { key: 'superacao', label: '💪 Superação' },
];

const DevotionalSubscribeWidget = () => {
  const [open, setOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [interests, setInterests] = useState(['familia', 'fe_esperanca']);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const saved = localStorage.getItem(SUBSCRIBED_KEY);
    if (saved) setSubscribed(true);
  }, []);

  const toggleInterest = (key) => {
    setInterests(prev =>
      prev.includes(key) ? prev.filter(i => i !== key) : [...prev, key]
    );
  };

  const formatPhone = (value) => {
    // Remove everything except digits
    return value.replace(/\D/g, '');
  };

  const validate = () => {
    const errs = {};
    if (!email && !phone) {
      errs.contact = 'Informe pelo menos e-mail ou WhatsApp';
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'E-mail inválido';
    }
    if (phone && phone.length < 10) {
      errs.phone = 'Número muito curto (inclua DDD)';
    }
    if (interests.length === 0) {
      errs.interests = 'Selecione pelo menos um tema';
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, phone, interests }),
      });
      if (!res.ok) throw new Error('Erro ao inscrever');
      localStorage.setItem(SUBSCRIBED_KEY, '1');
      setSubscribed(true);
      setOpen(false);
      setShowSuccess(true);
    } catch {
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title={subscribed ? 'Inscrito nos devocionais ✓' : 'Receber devocionais'} placement="right">
        <Fab
          onClick={() => setOpen(true)}
          size="medium"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            backgroundColor: subscribed ? '#4CAF50' : '#E67E22',
            color: 'white',
            '&:hover': { backgroundColor: subscribed ? '#388E3C' : '#CA6F1E' },
            zIndex: 1100,
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          }}
          aria-label="Inscrever-se para devocionais"
        >
          <FontAwesomeIcon icon={subscribed ? faCheck : faEnvelope} size="lg" />
        </Fab>
      </Tooltip>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 440 },
            maxHeight: '90vh',
            overflowY: 'auto',
            outline: 'none',
          }}
        >
          <Paper elevation={8} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ background: 'linear-gradient(135deg, #E67E22, #F39C12)', p: 2.5, position: 'relative' }}>
              <Button
                onClick={() => setOpen(false)}
                sx={{ position: 'absolute', top: 8, right: 8, minWidth: 0, color: 'white', p: 0.5 }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                📩 Receba Devocionais
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
                Reflexões diárias para fortalecer sua fé — por e-mail e/ou WhatsApp.
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="Seu nome (opcional)"
                value={name}
                onChange={e => setName(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                size="small"
                error={!!errors.email || !!errors.contact}
                helperText={errors.email}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="WhatsApp (com DDD, só números)"
                placeholder="Ex: 21999887766"
                value={phone}
                onChange={e => setPhone(formatPhone(e.target.value))}
                size="small"
                inputProps={{ maxLength: 13 }}
                error={!!errors.phone || !!errors.contact}
                helperText={errors.phone || errors.contact}
                sx={{ mb: 2.5 }}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Que temas te interessam?
              </Typography>
              <FormGroup sx={{ mb: 0.5 }}>
                {INTERESTS.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={interests.includes(key)}
                        onChange={() => toggleInterest(key)}
                        size="small"
                        sx={{ color: '#E67E22', '&.Mui-checked': { color: '#E67E22' } }}
                      />
                    }
                    label={<Typography variant="body2">{label}</Typography>}
                  />
                ))}
              </FormGroup>
              {errors.interests && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  {errors.interests}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  mt: 2.5,
                  backgroundColor: '#E67E22',
                  borderRadius: '20px',
                  py: 1.2,
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#CA6F1E' },
                }}
              >
                {loading ? 'Inscrevendo...' : 'Quero receber devocionais'}
              </Button>

              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#aaa' }}>
                Sem spam. Cancele a qualquer momento.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Modal>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)} sx={{ width: '100%' }}>
          🎉 Inscrito! Em breve você receberá devocionais. Que Deus abençoe!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShowError(false)} sx={{ width: '100%' }}>
          Erro ao inscrever. Tente novamente em instantes.
        </Alert>
      </Snackbar>
    </>
  );
};

export default DevotionalSubscribeWidget;
